import React, { useState } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Calendar, Clock, Download, Eye, FileText, FolderOpen, UserPlus, Users, BookOpen, Link2, CheckCircle } from 'lucide-react';
import InlineEdit from '@/components/InlineEdit';
import StudentLessonSubmit from './StudentLessonSubmit';
import { isVideoFile, safeDownload, formatScheduledDate } from '@/components/shared/contentHelpers';
import { PdfActivityDialog } from '@/components/PdfActivityDialog';
import DeleteContentMenu from '@/components/shared/DeleteContentMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonCard({ lesson, isStudent, token, t, onUpdated, onOpenSchedule, onReview, onDelete, onReuse, classStudents = [] }) {
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const videoFiles = (lesson.files || []).filter(f => f.file_type === 'video' || isVideoFile(f.filename));
  const activityFiles = (lesson.files || []).filter(f => f.file_type === 'activity' || (!f.file_type && !isVideoFile(f.filename)));
  const [openActivityFile, setOpenActivityFile] = useState(null);

  const saveInstructions = async (text) => {
    try {
      await axios.patch(`${API}/content/instructions/${lesson.id}`, { description: text }, headers);
      onUpdated(lesson.id, { description: text });
      toast.success(t('success'));
    } catch (err) {
      toast.error(t('error'));
      throw err;
    }
  };

  return (
    <div className="content-card bg-white rounded-xl p-5" data-testid={`lesson-${lesson.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-bold text-slate-800" style={{ fontFamily: 'Montserrat' }}>{lesson.title}</h4>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {lesson.folder && <Badge variant="secondary"><FolderOpen className="w-3 h-3 mr-1" />{lesson.folder}</Badge>}
            {!isStudent && lesson.scheduled_for && !lesson.is_published && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                <Clock className="w-3 h-3 mr-1" />
                {t('scheduled_for')}: {formatScheduledDate(lesson.scheduled_for)}
              </Badge>
            )}
            {!isStudent && lesson.assigned_to && lesson.assigned_to.length > 0 && (
              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                <Users className="w-3 h-3 mr-1" />{lesson.assigned_to.length} estudiante(s)
              </Badge>
            )}
            <Badge variant="outline" className="text-xs text-slate-400">
              {new Date(lesson.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
            </Badge>
          </div>
          <div className="mt-2">
            <InlineEdit
              value={lesson.description || ''}
              canEdit={!isStudent}
              placeholder="Sin instrucciones"
              onSave={saveInstructions}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isStudent && (
            <button onClick={() => onReuse(lesson)}
              className="text-slate-300 hover:text-emerald-500 transition-colors"
              title="Asignar a otros estudiantes"
              data-testid={`reuse-${lesson.id}`}>
              <UserPlus className="w-4 h-4" />
            </button>
          )}
          {!isStudent && (
            <button onClick={() => onOpenSchedule(lesson)}
              className="text-slate-300 hover:text-blue-500 transition-colors"
              title={t('edit_schedule')}
              data-testid={`edit-schedule-${lesson.id}`}>
              <Calendar className="w-4 h-4" />
            </button>
          )}
          {!isStudent && (
            <DeleteContentMenu
              contentId={lesson.id}
              classStudents={classStudents}
              token={token}
              onDelete={onDelete}
              onUnassigned={() => onUpdated(lesson.id, null)}
              testIdPrefix="delete-lesson"
            />
          )}
        </div>
      </div>

      {/* Videos */}
      {videoFiles.length > 0 && (
        <div className="space-y-3 mb-4">
          {videoFiles.map((f, i) => (
            <div key={f.filename || `vid-${i}`} className="rounded-lg overflow-hidden bg-slate-900">
              <video controls className="w-full max-h-96" preload="metadata" data-testid={`video-player-${lesson.id}-${i}`}>
                <source src={`${API}/files/${f.filename}`} type="video/mp4" />
                Tu navegador no soporta la reproducción de video.
              </video>
              <p className="text-xs text-slate-400 px-3 py-2">{f.original_name}</p>
            </div>
          ))}
        </div>
      )}

      {/* Activity files (1-click Blob download) */}
      {activityFiles.length > 0 && (
        <div className="mb-4">
          {/* Hide the plain download list for students once the interactive viewer is available */}
          {!(isStudent && lesson.bank_id) && (
            <>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('download_lesson')}</p>
              <div className="space-y-2">
                {activityFiles.map((f, i) => (
                  <div key={f.filename || `act-${i}`} className="space-y-1">
                    <button onClick={() => safeDownload(f.filename, f.original_name, token, t)}
                      className="w-full text-left flex items-center gap-2 p-2.5 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-sm text-blue-700"
                      data-testid={`download-file-${lesson.id}-${i}`}>
                      <Download className="w-4 h-4" />
                      <span className="font-medium">{f.original_name}</span>
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {/* Students with a bank_id go straight to the interactive viewer.
              Fase 2 · Once a student has already submitted the activity
              (`lesson.my_activity` populated by the backend content list),
              we replace the «Abrir actividad» CTA with a persistent
              confirmation message. The lock is based on server state so
              it survives page reloads, session changes and device swaps;
              drafts and «Retomar la actividad» remain untouched (they
              only apply while `my_activity` is still absent). */}
          {isStudent && lesson.bank_id && (
            lesson.my_activity ? (
              <div
                data-testid={`lesson-activity-submitted-${lesson.id}`}
                className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2"
              >
                <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Actividad entregada. El tutor revisará tu actividad y te enviará la corrección.
                </span>
              </div>
            ) : (
              <div className="space-y-2">
                {activityFiles.map((f, i) => (
                  !isVideoFile(f.filename) && (
                    <Button
                      key={f.filename || `open-${i}`}
                      onClick={() => setOpenActivityFile(f)}
                      data-testid={`open-lesson-btn-${lesson.id}-${i}`}
                      className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                    >
                      <BookOpen className="w-4 h-4" /> Abrir actividad
                    </Button>
                  )
                ))}
              </div>
            )
          )}
        </div>
      )}

      {/* Admin/teacher: link content ↔ bank_id */}
      {!isStudent && activityFiles.length > 0 && (
        <div className="mb-3">
          {lesson.bank_id ? (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
              <Link2 className="w-3 h-3 mr-1" /> bank_id: {lesson.bank_id.slice(0, 8)}…
            </Badge>
          ) : (
            <Button size="sm" variant="ghost" data-testid={`link-bank-btn-${lesson.id}`}
              onClick={async () => {
                const bid = window.prompt('Pega el bank_id de Feedback Hub para este PDF:');
                if (!bid) return;
                try {
                  await axios.post(`${API}/activities/content/${lesson.id}/link-bank`, { bank_id: bid.trim() }, headers);
                  toast.success('Enlazado a Feedback Hub');
                  onUpdated(lesson.id, { bank_id: bid.trim() });
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Error');
                }
              }} className="text-xs gap-1 text-slate-500">
              <Link2 className="w-3 h-3" /> Enlazar a Feedback Hub (bank_id)
            </Button>
          )}
        </div>
      )}

      {/* Legacy attach-and-submit is only shown when the interactive viewer is NOT available */}
      {isStudent && !lesson.bank_id && <StudentLessonSubmit lesson={lesson} token={token} t={t} onSubmitted={() => onUpdated(lesson.id, null)} />}

      {!isStudent && (
        <div className="border-t pt-3 mt-4">
          <Button variant="ghost" size="sm" onClick={() => onReview(lesson.id)} className="gap-1 text-blue-600" data-testid={`review-btn-${lesson.id}`}>
            <Eye className="w-4 h-4" /> {t('review_submissions')} ({lesson.submission_count || 0})
          </Button>
        </div>
      )}

      <PdfActivityDialog
        open={!!openActivityFile}
        onClose={() => setOpenActivityFile(null)}
        contentId={lesson.id}
        pdfFile={openActivityFile}
        token={token}
        requireAudioBeforeSubmit
        onActivitySubmitted={(data) => onUpdated(lesson.id, {
          my_activity: {
            activity_id: data?.activity_id,
            version: data?.version,
            submitted_at: new Date().toISOString(),
          },
        })}
      />
    </div>
  );
}

export { FileText }; // re-export for parent empty state
